import { lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useScrollTo } from 'hooks/use-scroll-to';

import styles from './bio.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const RichText = lazy(
	() => import('core/generic/components/general/rich-text')
);

type BioProps = {
	content: string;
	infoSectionHeight?: number;
};

export const Bio = ({ content, infoSectionHeight }: BioProps) => {
	const { scrollTopIfNeeded } = useScrollTo();
	const contentBodyRef = useRef<HTMLDivElement>(null);
	const [condensed, setCondensed] = useState(false);
	const [isTooLong, setIsTooLong] = useState(false);
	const [bioHeight, setBioHeight] = useState<number | undefined>();
	const { t } = useTranslation();

	useEffect(() => {
		setIsTooLong(false);
		setCondensed(false);
		const measure = () => {
			if (contentBodyRef.current) {
				const h = contentBodyRef.current.clientHeight;
				const w = window.innerWidth;
				const tooLong =
					(w < 768 && h > 512) ||
					(w < 1136 && h > 384) ||
					h > infoSectionHeight!;
				setIsTooLong(tooLong);
				setCondensed(tooLong);

				setBioHeight(w >= 1136 ? infoSectionHeight : undefined);
			}
		};

		const timeout = setTimeout(measure, 300);

		return () => {
			clearTimeout(timeout);
		};
	}, [content, infoSectionHeight]);

	const handleMoreLessClick = () => {
		if (!condensed) {
			scrollTopIfNeeded({
				bodyRef: contentBodyRef,
			});
		}
		setCondensed(!condensed);
	};

	return (
		<div
			className={`${styles['bio']} ${
				condensed ? styles['bio--condensed'] : ''
			}`}
			ref={contentBodyRef}
			style={
				condensed && bioHeight
					? { maxHeight: `${bioHeight}px` }
					: undefined
			}
		>
			<RichText content={content} placeholder={' '} />

			{isTooLong && (
				<div className={styles['bio__read-more']}>
					<Button
						dimension="small"
						label={
							condensed
								? t('players_profile.read_more', 'Read more')
								: t('players_profile.read_less', 'Read less')
						}
						tabIndex={0}
						variant="tertiary"
						icon={condensed ? 'ChevronDown' : 'ChevronUp'}
						iconPosition="right"
						onClick={() => {
							handleMoreLessClick();
						}}
					/>
				</div>
			)}
		</div>
	);
};
